import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { createPayPalOrder, capturePayPalOrder, calculateFinalAmount } from '../lib/paypal';
import { supabase } from '../lib/supabase';
import TransactionBreakdown from './TransactionBreakdown';
import type { RegistryItem, RSVPResponse } from '../types/registry';

// Currencies that PayPal does not support natively and must be converted to USD
const PAYPAL_UNSUPPORTED_CURRENCIES = new Set(['LKR']);
const USD_CONVERSION_RATES: Record<string, number> = { LKR: 320 };

interface Props {
  item: RegistryItem;
  currency: string;
  onClose: () => void;
  onSuccess: () => void;
  rsvpData?: RSVPResponse;
  feeResponsibility?: 'COUPLE' | 'GUEST' | 'SPLIT';
  platformFeePercentage?: number;
  gatewayFeePercentage?: number;
}

export default function PaymentModal({
  item,
  currency,
  onClose,
  onSuccess,
  rsvpData,
  feeResponsibility = 'GUEST',
  platformFeePercentage,
  gatewayFeePercentage
}: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPayPal, setShowPayPal] = useState(false);
  const [registryDetails, setRegistryDetails] = useState<{
    fee_responsibility: string;
    platform_fee_percentage: number;
    gateway_fee_percentage: number;
    user_id: string;
    paypal_email?: string | null;
  } | null>(null);
  const [userInfo, setUserInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: ''
  });

  useEffect(() => {
    loadRegistryDetails();
  }, []);

  useEffect(() => {
    if (rsvpData) {
      const nameParts = rsvpData.name.split(' ');
      setUserInfo(prev => ({
        ...prev,
        firstName: nameParts[0] || '',
        lastName: nameParts.slice(1).join(' ') || '',
        email: rsvpData.email || '',
        phone: rsvpData.phone || ''
      }));
    }
  }, [rsvpData]);

  const loadRegistryDetails = async () => {
    try {
      const { data: registry, error } = await supabase
        .from('registries')
        .select('fee_responsibility, platform_fee_percentage, gateway_fee_percentage, user_id, paypal_email')
        .eq('id', item.registry_id)
        .single();

      if (error) throw error;
      if (registry) {
        setRegistryDetails(registry);
      }
    } catch (err) {
      console.error('Error loading registry details:', err);
    }
  };

  const handleShowPayPal = (e: React.FormEvent) => {
    e.preventDefault();
    if (!userInfo.firstName || !userInfo.lastName || !userInfo.email || !userInfo.phone || !userInfo.address || !userInfo.city) {
      setError('Please fill in all required fields');
      return;
    }
    setError('');
    setShowPayPal(true);
  };

  const effectiveFeeResponsibility = registryDetails?.fee_responsibility || feeResponsibility || 'GUEST';
  const effectivePlatformFee = registryDetails?.platform_fee_percentage || platformFeePercentage || 0;
  const effectiveGatewayFee = registryDetails?.gateway_fee_percentage || gatewayFeePercentage || 0;
  const registryOwnerId = registryDetails?.user_id || '';
  const payeeEmail = registryDetails?.paypal_email || undefined;

  const needsConversion = PAYPAL_UNSUPPORTED_CURRENCIES.has(currency);
  const paypalCurrency = needsConversion ? 'USD' : currency;
  const conversionRate = needsConversion ? (USD_CONVERSION_RATES[currency] ?? 1) : null;

  const lkrUsdAmount = needsConversion && conversionRate
    ? (calculateFinalAmount(
        item.price,
        effectiveFeeResponsibility as 'COUPLE' | 'GUEST' | 'SPLIT',
        effectivePlatformFee,
        effectiveGatewayFee
      ).finalAmount / conversionRate).toFixed(2)
    : null;

  return (
    <div className="fixed inset-0 bg-black/75 flex items-center justify-center p-4 z-50">
      <div className="w-full max-w-[1000px] bg-gray-800/95 backdrop-blur-sm rounded-lg overflow-hidden">
        <div className="bg-gray-900/50 p-6 border-b border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl md:text-2xl font-semibold text-white">Claim Gift</h3>
              <p className="mt-2 text-gray-300">Complete your information to claim {item.name}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-700 rounded-full transition-colors text-gray-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 max-h-[calc(100vh-200px)] overflow-y-auto">
          {error && (
            <div className="mb-6 bg-red-900/50 text-red-200 p-4 rounded-md">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-6">
              <div className="bg-gray-900/50 p-4 rounded-lg">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-gray-300">Item</p>
                    <p className="text-lg font-semibold text-white">{item.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-300">Price</p>
                    <p className="text-lg font-semibold text-white">{currency} {item.price.toFixed(2)}</p>
                  </div>
                </div>
              </div>

              {!showPayPal ? (
                <form onSubmit={handleShowPayPal} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">First Name *</label>
                      <input
                        type="text"
                        value={userInfo.firstName}
                        onChange={(e) => setUserInfo({ ...userInfo, firstName: e.target.value })}
                        className="w-full px-4 py-3 bg-gray-900/50 border border-gray-700 text-white rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
                        placeholder="Enter your first name"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Last Name *</label>
                      <input
                        type="text"
                        value={userInfo.lastName}
                        onChange={(e) => setUserInfo({ ...userInfo, lastName: e.target.value })}
                        className="w-full px-4 py-3 bg-gray-900/50 border border-gray-700 text-white rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
                        placeholder="Enter your last name"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Email *</label>
                    <input
                      type="email"
                      value={userInfo.email}
                      onChange={(e) => setUserInfo({ ...userInfo, email: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-900/50 border border-gray-700 text-white rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
                      placeholder="Enter your email address"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Phone *</label>
                    <input
                      type="tel"
                      value={userInfo.phone}
                      onChange={(e) => setUserInfo({ ...userInfo, phone: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-900/50 border border-gray-700 text-white rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
                      placeholder="Enter your phone number"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Address *</label>
                    <input
                      type="text"
                      value={userInfo.address}
                      onChange={(e) => setUserInfo({ ...userInfo, address: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-900/50 border border-gray-700 text-white rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
                      placeholder="Enter your address"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">City *</label>
                    <input
                      type="text"
                      value={userInfo.city}
                      onChange={(e) => setUserInfo({ ...userInfo, city: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-900/50 border border-gray-700 text-white rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
                      placeholder="Enter your city"
                      required
                    />
                  </div>
                  <div className="flex justify-end space-x-4 pt-4">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-6 py-3 text-gray-300 hover:text-white bg-gray-700/50 rounded-md hover:bg-gray-600/50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-6 py-3 bg-primary text-white rounded-md hover:bg-primary-600 transition-colors"
                    >
                      Proceed to Payment
                    </button>
                  </div>
                </form>
              ) : (
                <div className="space-y-4">
                  <div className="bg-gray-900/50 p-4 rounded-lg">
                    <p className="text-sm text-gray-300 mb-1">Paying as</p>
                    <p className="text-white font-medium">{userInfo.firstName} {userInfo.lastName}</p>
                    <p className="text-gray-400 text-sm">{userInfo.email}</p>
                  </div>

                  {needsConversion && lkrUsdAmount && (
                    <div className="bg-blue-900/30 border border-blue-700/50 rounded-md p-3">
                      <p className="text-xs text-blue-300 text-center">
                        PayPal processes {currency} as USD. You will be charged{' '}
                        <span className="font-semibold text-white">USD {lkrUsdAmount}</span>{' '}
                        (1 USD = {conversionRate} {currency}).
                      </p>
                    </div>
                  )}

                  <div className="bg-white rounded-lg p-4">
                    <PayPalScriptProvider options={{ clientId: import.meta.env.VITE_PAYPAL_CLIENT_ID, currency: paypalCurrency }}>
                      <PayPalButtons
                        style={{ layout: 'vertical', color: 'blue', shape: 'rect', label: 'pay' }}
                        createOrder={async () => {
                          setLoading(true);
                          setError('');
                          const response = await createPayPalOrder({
                            itemId: item.id,
                            itemName: item.name,
                            originalAmount: item.price,
                            currency: currency,
                            feeResponsibility: effectiveFeeResponsibility as 'COUPLE' | 'GUEST' | 'SPLIT',
                            platformFeePercentage: effectivePlatformFee,
                            gatewayFeePercentage: effectiveGatewayFee,
                            registryId: item.registry_id,
                            registryOwnerId: registryOwnerId,
                            payerFirstName: userInfo.firstName,
                            payerLastName: userInfo.lastName,
                            payerEmail: userInfo.email,
                            payeeEmail: payeeEmail,
                          });
                          return response.orderId;
                        }}
                        onApprove={async (data) => {
                          try {
                            await capturePayPalOrder(data.orderID);
                            onSuccess();
                          } catch (err: any) {
                            setError(err.message || 'Payment capture failed');
                            setLoading(false);
                          }
                        }}
                        onError={(err) => {
                          console.error('PayPal error:', err);
                          setError('Payment failed. Please try again.');
                          setLoading(false);
                        }}
                        onCancel={() => {
                          setLoading(false);
                        }}
                      />
                    </PayPalScriptProvider>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowPayPal(false)}
                    className="w-full px-6 py-3 text-gray-300 hover:text-white bg-gray-700/50 rounded-md hover:bg-gray-600/50 transition-colors"
                  >
                    Back to Edit Details
                  </button>
                </div>
              )}
            </div>

            <div className="space-y-6">
              {registryDetails && (
                <TransactionBreakdown
                  originalAmount={item.price}
                  currency={currency}
                  showDetailed={false}
                  feeResponsibility={effectiveFeeResponsibility}
                  platformFeePercentage={effectivePlatformFee}
                  gatewayFeePercentage={effectiveGatewayFee}
                />
              )}
              <div className="bg-gray-800/30 p-3 rounded-md">
                <p className="text-sm text-gray-300 text-center">
                  Your payment is secure and processed by PayPal
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
